/**
 * @module Login Service
 */

import {CONNECT} from '@acng/frontend-stargazer';
import {query} from '@acng/frontend-bounty/dom/query.js';
import {INPUT} from '@acng/frontend-bounty/dom/element.js';
import {popup} from '@acng/frontend-discovery';
import {now} from '@acng/frontend-bounty/std/date.js';
import {guard, NULLABLE, OPTIONAL, INTERFACE, IS_INPUT} from '@acng/frontend-rubicon';
import {location, service} from 'nova';
/** @import {SubmitAction} from 'nova' */
import {user} from '../user.js';
import {get, post} from '../../core/service/backend.js';
import {inject, setInjector} from '../../core/service/ng.js';
import {t} from '../../locale/config/translate.js';
import {AUTH_USER_DATA} from '../model/user-data.js';
import {authUser, make} from '../../core/service/env.js';
import {triggerPopups} from '../../userPool/service/director.js';
import {assign} from '@acng/frontend-bounty/object.js';
import {fsk} from '../../userPool/fsk.js';
import {useLocale} from '../../locale/model/locale.js';
import {provideAuthUser} from '../../userPool/context/auth-user.js';
import {replaceUrl} from '@acng/frontend-relativity/history';
import {FormController, setPasswordInput, setUsernameInput} from 'nova';
import {iOS} from '../../core/constant/iOS.js';
import {TwoFactorLogin} from './two-factor-login.js';
import {getLocalOverlay} from '../../core/service/overlay.js';
import {PasswordMail} from './mail-password.js';

const url = 'user/login';

/**
 * Time of Last Login
 *
 * - Timestamp, in milliseconds as returned by `Date.now()`
 *
 * @example 1765883018266
 */
let loginTime = Infinity;

/**
 * @param {import('angular').auto.IInjectorService} $injector
 * @param {import('angular').ILocationService} $location
 */
const initialLogin = async ($injector, $location) => {
  setInjector($injector);

  /**
   * @type {Valid<AUTH_USER_DATA> | null | undefined}
   */
  let userData = authUser;

  const reactivate = /\/user\/reactivate\/(.+)/.exec($location.path());

  if (reactivate) {
    const [, key] = reactivate;
    const url = 'user/reactivate';
    userData = await post(url, {key});
    ASSERT: guard(userData, AUTH_USER_DATA, `decoded ${url} response body`);

    userData.redirectPath = '/';
    await popup().info(await t('userPool.accountReactivatedLong'));
  }

  if (userData) {
    performLogin(userData);
  }
};

/**
 * @param {Valid<AUTH_USER_DATA>} userData
 *
 * @returns {void}
 */
const performLogin = (userData) => {
  loginTime = now();

  let path = userData.redirectPath;

  if (!path && /(login|signup|reactivate)/.test(location.hash)) {
    path = '/';
  }

  if (path) {
    replaceUrl(`/#${path}`);
  }

  if (userData.id != user.id) {
    user.guest = false;
    assign(user, userData);
    provideAuthUser(user);
    fsk.set(user.fsk);
    useLocale(user.preferred_language);
  }

  triggerPopups();
  inject('$rootScope').$broadcast('userPool.login.success', userData);
};

/**
 * @param {Element} [element]
 *
 * @returns {Promise<void>}
 */
const restore = async (element) => {
  const data = await get('user/auth-user', element);
  ASSERT: guard(data, AUTH_USER_DATA);
  performLogin(data);
};

/**
 * Perform a user login.
 *
 * @param {HTMLElement} element
 * @param {FormData} credentials
 */
const login = async (element, credentials) => {
  const onsOverlay = inject('onsOverlay');

  try {
    const authUserData = await post(url, credentials, element, async (error) => {
      switch (error.data.code) {
        case 1007:
          onsOverlay.create('confirm-device').open();
          return null;
        case 1009:
          popup(element).warn(await t('userPool.reactivationMailSent'));
          return null;
      }

      if (!onsOverlay.count()) {
        onsOverlay.create('pageLogin').disableBackdrop().open();
      }

      throw error;
    });

    ASSERT: guard(authUserData, RESPONSE, `decoded ${url} response body`);

    if (authUserData) {
      performLogin(authUserData);
    }

    getLocalOverlay(element)?.close();

    const {href} = element.dataset;

    if (authUser && href) {
      location.assign(href);
    }

    return authUserData;
  } catch (reason) {
    // No further treatment is necessary.
  }
};

/**
 * | name     | type     | autocomplete     | validation |
 * | -------- | -------- | ---------------- | ---------- |
 * | login    | text     | username         | required   |
 * | password | password | current-password | required   |
 * | remember | checkbox | off              |            |
 *
 * The "remember" field is optional and the remember cookie defaults to being
 * set.
 *
 * @example
 *
 * ```html
 * <!--@ ${auth.login} -->
 * <form>
 *   <input name="login" />
 *   <input name="password" />
 *   <input name="remember" />
 *   <input type="submit" />
 * </form>
 * ```
 */
@service
class UserLogin extends FormController {
  /**
   * Send the password reset token mail.
   */
  mail = new PasswordMail();

  /**
   * Login with two factor code.
   */
  twoFactor = new TwoFactorLogin();

  /**
   * @param {HTMLFormElement} form
   */
  arrange(form) {
    ASSERT: {
      const CONTROLS = INTERFACE({
        login: IS_INPUT,
        password: IS_INPUT,
        remember: OPTIONAL(IS_INPUT),
      });

      guard(form.elements, CONTROLS);
    }

    const {login, password, remember} = form.elements;

    setUsernameInput(login);
    setPasswordInput(password, true);

    if (remember) {
      remember.type = 'checkbox';
      remember.autocomplete = 'off';
    }
  }

  /** @override @param {Element} form */
  [CONNECT](form) {
    super[CONNECT](form);

    if (!iOS && getLocalOverlay(form)) {
      query(form, INPUT)?.focus();
    }
  }

  /** @override @type {SubmitAction} */
  async submit(form, data) {
    if (!form.elements.namedItem('remember')) {
      data.append('remember', 'on');
    }

    await login(form, data);
  }
}

/**
 * Validate the decoded login response body.
 */
const RESPONSE = /* @__PURE__ */ NULLABLE(AUTH_USER_DATA);

export {performLogin, initialLogin, loginTime, UserLogin};
